import type { EssentialGroup } from './types';

export const PANEL_STATE_KEY = 'bipg-state';
export const BIPG_FEEDBACK_GROUP_ID = 'jira-servicedesk-better-in-product-guidance-panel';

// Actual content tree/format still TBD so the following structure/copy is placeholder and not wrapped in formatMessage yet.
export const essentialsGroup: EssentialGroup[] = [
	'Request types',
	'Custom fields',
	'Forms',
	'Portal groups',
	'Workflows',
];

export const stepByStepGroup = [
	'Create a request type',
	'Customize a request type',
	'Duplicate a request type',
	'Add a form to a request type',
	'Replace a request type’s workflow',
	'Make request types visible to customers in the help center',
	'Organize request types into portal groups',
	'Add a new workflow to your request type using workflow templates',
	'Test your request types',
	'Delete a request type',
];

export const bestPracticesGroup = [
	'What are some best practices for setting up request types?',
	'Which request types should I create?',
	'How many fields should I add to a request type?',
	'When should I use an existing custom field vs a new one?',
	'When should I add a form to a request type?',
	'How many request types do I need?',
	'What’s the problem with creating many custom fields?',
	'What do I need to know about linking form fields and Jira fields?',
	'How should I configure portal groups?',
	'How should I create workflows?',
];

export type CONTENT_TYPE = 'conceptual' | 'procedural' | 'best-practices';

export const allQuestions: { question: string; answer: string; contentType: CONTENT_TYPE }[] = [
	{
		question: 'What are request types?',
		answer: `Request types are used to define and categorize the different types of requests that your customers will have. <br/>
        <div style="padding:5px"></div>
        When customers submit a request via your project’s active channels (eg, email, chat, or portal) they will select the relevant request type (eg, Employee onboarding) and fill in the fields of the request form, providing your team with the information they need to manage and resolve the request efficiently.<br/>
        <div style="padding:5px"></div>    
        Each request type is linked to an issue type, which primarily provides the workflow for the request type. Go to <b>Project settings</b> then <b>Request types</b> to review and edit your request types.<br/>
        <div style="padding:5px"></div>
        Or add new ones by customizing a blank request type, using a request type template as a starting point, or using AI to suggest request types based on your inputs (AI option is only available to JIra admins using Atlassian Intelligence on Premium or Enterprise editions).`,
		contentType: 'conceptual',
	},
	{
		question: "What's the difference between a request type and an issue type?",
		answer: `While each request type is linked to an issue type, the difference between them lies in their use and visibility.<br/>
        <div style="padding:5px"></div>
        The request type defines the help-seeker and agent experiences (ie, the intake form and issue view). It helps to categorize an incoming request and is configured with specific fields to collect the necessary information about the request. Each request type can only be connected to one issue type at a time.<br/>
        <div style="padding:5px"></div>
        An issue type, on the other hand, provides the underlying configuration. It defines the workflow that a request follows and the fields that are available to its request types. One issue type can be used for many different request types.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'How many request types do I need?',
		answer: `The number of request types you need depends on the variety of work your team does and the distinct processes or information required for each. Aim for a balance that covers all necessary service areas without overwhelming users with too many options. <br/>
        <div style="padding:5px"></div>
        We recommend starting with fewer request types and adding more as you need them, aiming for no more than 10 to 15 at most. Consider combining similar services under broader request types using conditional fields to keep the portal uncluttered. <br/>
        <div style="padding:5px"></div>
        Regularly review and adjust your request types to ensure your request catalogue remains effective and user-friendly.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'Which request types should I create?',
		answer: `Create request types that reflect the things that your customers typically ask you or your team for.<br/>
        <div style="padding:5px"></div>
        Review the requests you currently receive, identify which of these are most common, and decide how you might group or categorize them. This will inform how many and what kinds of request types you might need, and how you might use request types to sort incoming requests into the right queues.<br/>
        <div style="padding:5px"></div>
        From <b>Project settings</b> select <b>Request types</b>, then <b>Create request type</b> and <b>Create from template</b> to browse the request type template library, which provides ideas and a starting point for request types that you can customize later to meet your needs.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'What are request type templates?',
		answer: `
        Request type templates help to make request type creation quicker and easier. Instead of creating a request type from scratch, you’ll have the option to browse a library of templates based on requests that are common to teams like customer service, IT, HR, and beyond.<br/>
        <div style="padding:5px"></div>
        From <b>Project settings</b> select <b>Request types</b>, then <b>Create request type</b> and <b>Create from template</b> to browse the request type template library. Select a template to create a pre-configured request type that you can customize later to meet your needs.<br/>
        <div style="padding:5px"></div>
        These templates are a good starting point for Jira admins who are new to Jira Service Management, as well as Jira admins who are looking to create consistency across multiple projects.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What are some best practices for setting up request types?',
		answer: `Here’s some recommendations to help with the successful setup of request types:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>For request type names, use keywords and clear, non-technical language familiar to your customers.</li>
        <li>For request type descriptions, consider using examples to make it obvious when the request type should be used.</li>
        <li>Create a range of request types that cater to your customers' needs, without having too many to choose from.</li>
        <li>Use icons to differentiate request types in the portal so they can be easily identified.</li>
        <li>Tailor the fields of each request type to gather all necessary information upfront, and include only the necessary fields to keep the form simple and intuitive.</li>
        <li>Leverage custom fields or attach a form to help capture specific details relevant to each request.</li>
        <li>Map each request type to an appropriate issue type to ensure the right workflow is triggered.</li>
        <li>Organize request types into portal groups or topics for easier navigation and to make it easier for customers to find what they need.</li>
        <li>And continually refine configurations based on user feedback and evolving service needs.</li>
        <ul style="padding-left: 20px;list-style-position: outside;">`,
		contentType: 'best-practices',
	},
	{
		question: 'How many fields should I add to a request type?',
		answer: `Strike a balance with the number of fields in a request type, so that the form is concise and easy for your customers to complete while collecting all the essential information to manage and resolve the request.<br/>
        <div style="padding:5px"></div>
        Start with essential fields like 'Summary' and 'Description', and add other fields necessary for resolving the request. We suggest adding specific fields where appropriate because free-text fields can impact reporting.<br/>
        <div style="padding:5px"></div>
        Also, make sure any instructions are clear and that each question (or field label) is easy to understand. The easier the form is to complete, the more likely it is that your customers will provide accurate and complete information.<br/>
        <div style="padding:5px"></div>
        Remember, you can always edit your request types later to meet your changing needs.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'What is the request type name and description?',
		answer: `The request type name and description clearly communicate the request type’s purpose to your customers.<br/>
        <div style="padding:5px"></div>
        The request type name is a short, clear label that makes it easy for users to identify and select the correct request type for their needs. The name should be specific and ideally no more than a few words, for example ‘Request new feature’.<br/>
        <div style="padding:5px"></div>
        The description appears under the request type name in the portal. It should be concise yet detailed enough so that customers can understand when and why the request type should be used.<br/>
        <div style="padding:5px"></div>
        Both the name and description should be written in plain language that your customers will understand, so avoid using internal jargon or technical terms.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What’s the difference between the request form and the issue view?',
		answer: `
        The request form is what your customers interact with when they submit a request. They choose a request type and fill in the form fields designed to capture essential information about the request.<br/>
        <div style="padding:5px"></div>
        Once submitted, the request becomes an issue for your team members (or agents) to work on, and the data from the request form fields map to the fields in the issue view. <br/>
        <div style="padding:5px"></div>
        The issue view is what your agents see when they're working on the request.<br/>
        <div style="padding:5px"></div>
        Along with the information the customer has provided in the request form, this view can contain more detailed fields and information that are useful for the agent but might be too technical or unnecessary for the customer.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What’s the relationship between request types and workflows?',
		answer: `Request types and workflows are closely linked, but they don’t have a direct relationship. Their connection is mediated by issue types, because each request type is linked to an issue type, and the issue type is associated with a workflow.<br/>
        <div style="padding:5px"></div>
        So the choice of issue type for a request type determines the workflow that requests of that type will follow. If you go to <b>Project settings</b> then <b>Request types</b>, you’ll be able to see the issue type and workflow associated with each request type.<br/>
        <div style="padding:5px"></div>            
        It’s worth noting that the mapping of issue types and workflows is typically defined at a project level, so the same issue type may not map to the same workflow across different projects.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'Create a request type',
		answer: `To create and configure a new request type:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Request types.</b></li>
        <li>Select <b>Create request type.</b></li>
        </ol>
        <div style="padding:5px"></div>
        From here, you have two options:<br/>
        <div style="padding:5px"></div>
        Create a blank request type that you can customize to suit your needs:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Select <b>Create blank.</b></li>
        <li>Enter the request type name, description, icon, portal group, and issue type.</li>
        <li>Select <b>Save.</b></li>
        </ol>
        <div style="padding:5px"></div>
        Create a request type using a template as a starting point (Jira admins only):<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Select <b>Create from template.</b></li>
        <li>Browse, search, and <b>Preview</b> the different templates.</li>
        <li><b>Select</b> the template you want to use to create your new request type.</li>
        <li>Review and update the request type’s details including name, description, icon, portal group, and issue type.</li>
        <li>Select <b>Save</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Jira admins who are using Atlassian Intelligence on the Premium or Enterprise editions of Jira Service Management will see the third option to <b>Create using AI</b>.<br/>
        <div style="padding:5px"></div>
        For any of these methods, the request type object will be created and associated with the selected issue type and workflow. You’ll then need to determine what fields will be displayed to both your customers and your agents.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Customize a request type',
		answer: `To customize a request type’s name, description, icon, and fields of the request form or issue view, you’ll need Jira admin or project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Request types.</b></li>
        <li>Find and select the name of the request type you want to edit.</li>
        <li>Edit the request type’s name, description, and icon.</li>
        <li>Select either the <b>Request form</b> or <b>Issue view</b> tabs above the request type name.</li>
        <li>Add, remove, and rearrange fields as desired:
            <ul style="padding-left: 20px;list-style-position: outside;">    
            <li>to add a field, drag and drop it into your desired location from the panel on the right.</li>
            <li>to remove a field, drag and drop it into the panel on the right.</li>
            <li>to rearrange fields, drag and drop them into your desired order.</li>
            </ul>
        </li>       
        <li>When configuring your <b>Request form</b>, you can also:
            <ul style="padding-left: 20px;list-style-position: outside;">
            <li>change the display name of the field in the request form under <b>Display name.</b></li>
            <li>add a description to your field under <b>Display description.</b></li>
            <li>automatically fill in a field and hide it from customers in the request form (and portal) by checking the box next to <b>Use preset value and hide from portal.</b></li>
            <li>make it compulsory to fill in a field by checking the box next to <b>Required</b> (some fields are required by default).</li>
            </ul>
        </li>
        <li>When you’re done, select <b>Save changes.</b></li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Add a form to a request type',
		answer: `You can add a form to a request type two different ways.<br/>
        <div style="padding:5px"></div>
        To add directly from request types:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Go to <b>Project settings</b>, then <b>Request types</b>.</li>
        <li>Select a request type.</li>
        <li>Select <b>Attach form</b> at the bottom of the request form.</li>
        <li>Select either <b>Create from template</b> if you wish to create a new form, or <b>Select existing</b> if you wish to use a form already in your project.</li>
        <li>Select <b>Save changes</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        To add from the form builder settings:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Go to <b>Project settings</b>, then <b>Forms</b>.</li>
        <li>Select a form.</li>
        <li>Select the <b>Settings</b> tab.</li>
        <li>Turn on the toggle for <b>Attach to request types</b>.</li>
        <li>From the drop-down list, select your desired request types.</li>
        <li>Select <b>Save changes</b>.</li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Replace a request type’s workflow',
		answer: `You can replace the workflow of any of your request types with another existing workflow from any service project on your Jira Service Management site.<br/>
        <div style="padding:5px"></div>
        As each request type is linked to an issue type which determines the request type’s workflow, replacing a request types’s workflow will also change it’s assocciated issue type.<br/>
        <div style="padding:5px"></div>
        You need to be a Jira administrator to replace the workflow of a request type.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project Settings</b>, then <b>Request types.</b></li>
        <li>For the request type you’d like to update, select <b>More actions (...)</b> then <b>Replace with existing.</b></li>
        <li>In the replace workflow panel on the right, open the <b>Filter by project</b> menu and select the project your desired workflow is in.</li>
        <li>Open the <b>Select workflow</b> menu and choose the workflow you’d like to use. Sometimes, not all your workflows will be available to select. Workflows will be filtered out of the list if using them would cause a conflict in your current project.</li>            
        <li>If needed, select a new issue type from the <b>Change issue type</b> menu. You can choose from any issue types that have been assigned to your selected workflow.</li>
        <li>Select <b>Update.</b></li>
        <li>If you need to update the workflow statuses for any of your existing issues (for example if your previous workflow has statuses that don't exist in the new workflow), we’ll prompt you to select new statuses to replace them. Select <b>Confirm</b> when you’re done.</li>
        <li>If there are are a large number of issues to update a loading bar will appear. Keep this page open and continue working in another tab until the update is 100% complete.</li>
        <li>When complete, close the tab or select <b>Go to Request types</b> to return to the <b>Request types</b> page in your <b>Project Settings.</b></li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Add a new workflow to your request type using workflow templates',
		answer: `You need to be a Jira administrator to use workflow templates to create a new pre-configured workflow that will be linked to your request type.<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project settings</b>, then <b>Request types.</b></li>
        <li>Select the name of the request type you want to update and within that request type's configuration screen, select <b>Manage workflow</b>, then select <b>Replace using template.</b></li>
        <li><b>Preview</b> a template to see its description, statuses, and transitions.</li>
        <li><b>Select</b> the template you want to use, then review and make desired changes to the names of your new workflow and issue type.</li>
        <li>Select <b>Continue</b> and the new workflow and issue type will be added to your project and linked to the request type you chose to update.</li>
        </ul>
        <div style="padding:5px"></div>
        You can also replace a workflow using a template from the <b>Request types</b> screen by selecting <b>More actions (...)</b> next to the request type you want to update. Then in the <b>Workflow</b> section, select <b>Replace using template.</b><br/>
        <div style="padding:5px"></div>
        Instead of replacing a request type’s workflow with a new one, you might prefer to replace it with another workflow that already exists in other service projects within your Jira Service Management site.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Organize request types into portal groups',
		answer: `Make your request types (and their forms) visible in the appropriate portal groups so your customers can easily find what they need.<br/>
        <div style="padding:5px"></div>
        You need to be a Jira or project administrator to organize request forms into portal groups.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project settings</b>, then <b>Portal settings.</b></li>
        <li>Select the <b>Portal groups</b> tab.</li>
        <li>Use the arrow to open the group you want to add the request types to, or select <b>Create group</b> to create a new group.</li>
        <li>Select <b>+Add request form</b> and use the search bar to select an existing request type to add. You can add a request type to more than one group.</li>
        <li>Drag and drop request types to rearrange them within your groups.</li>
        <li>Drag and drop groups to rearrange them in your portal.</li>
        </ol>
        <div style="padding:5px"></div>
        Note that you’ll need more than one group for groups to appear in the portal. Forms that aren't assigned to a group will be hidden from your portal.<br/>
        <div style="padding:5px"></div>
        You can also add a request form to a portal group by:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Going to <b>Project Settings</b>, then <b>Request types.</b></li>
        <li>Select <b>More actions (...)</b> next to the request type, then in the <b>Portal group</b> section select <b>Edit.</b></li>
        <li>Select <b>Create group</b> to create a new group or select the portal group(s) you want the request type to appear in, then select <b>Save.</b></li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Make request types visible to customers in the help center',
		answer: `You can make request types (and their forms) visible to customers within a portal in the help center by assigning them to portal groups:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Request types.</b> The portal groups column displays where each request type appears, or if it is hidden from the portal.</li>
        <li>Select <b>More actions (...)</b> next to the request type you want to show in the portal, then select <b>Edit</b> in the portal groups section.</li>
        <li>Check the boxes to assign the request type to portal groups, or to hide the request type from your customers, leave all groups unselected. Select <b>Create group</b> if you want to add a new portal group.</li>
        <li>Select <b>Save.</b></li>
        </ol>
        <div style="padding:5px"></div>
        By making a request type visible in the portal, customers can use it to raise requests. If a request type is not visible in the portal, only agents and admins can use it to create requests.<br/>
        <div style="padding:5px"></div>
        You can use topics to group request forms (and knowlege base articles and external resources) from across portals. Adding request forms to topics will let them show up on the help center home page.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Duplicate a request type',
		answer: `To duplicate a request type you’ll need project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, and then <b>Request types.</b></li>
        <li>Select <b>More actions (...)</b> next to the request type you want to duplicate.</li>
        <li>Select <b>Duplicate.</b></li>
        <li>Add a name, description and icon for your new request type.</li>
        <li>Select <b>Next.</b></li>
        <li>Select a portal group for your new request type, then select <b>Create.</b></li>
        </ol>
        <div style="padding:5px"></div>
        Once created, your new request type will have the same configuration as the original request type. However, it does not copy any associated forms, and any JQL set to the Request Type level will need to be updated.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Test your request types',
		answer: `To test your request types, begin by raising a request from the portal as if you were a customer:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>On the <b>Request types</b> screen in project settings, select the name of the request type you want to test. Ensure the request type is associated with at least one portal group.</li>
        <li>At the bottom of the request type’s configuration screen, select <b>View</b> to go to your service portal, the view your customers will see.</li>
        <li>Fill in the request form fields, then select <b>Send</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Now view the request as if you were an agent:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>At the top of the request summary, select the <b>Issue key</b> of the request you just created. It’s the combination of letters and numbers that helps you to identify this request. The link will open up the issue view, which is what agents see.</li>
        <li>Check that the information in the issue view has been captured and displayed correctly, and that the desired workflow has been triggered. Also check if any necessary fields are missing or if there's any unnecessary information.</li>
        <li>Repeat this process for all request types.</li>
        <li>To test permissions, consider logging in as different user types using dummy accounts.</li>
        </ol>
        <div style="padding:5px"></div>
        Testing your request types helps to ensure the experience is user-friendly and that you’re capturing the information needed to resolve the request.<br/>
        <div style="padding:5px"></div>
        Getting things exactly right takes time, so be patient! It might take three or more iterations but the valuable testing and feedback loop will ensure you end up with an experience that gets the buy-in of your team, colleagues, and customers.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Delete a request type',
		answer: `To delete a request type you’ll need project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Request types.</b></li>
        <li>Select <b>More actions (...)</b> next to the request type you wish to delete.</li>
        <li>Select <b>Delete.</b></li>
        </ol>
        <div style="padding:5px"></div>
        Note that deleting a request type will not remove any existing issues of that type. It only prevents new requests of that type from being created.<br/>
        <div style="padding:5px"></div>
        If you have requests currently using the request type you want to delete, we'll warn you before you delete it. You’ll have to update these requests to use a new request type, as any details or fields unique to the deleted request type will no longer be visible.<br/>
        <div style="padding:5px"></div>
        If you don’t move the affected requests to a new request type, they won’t be visible in the portal or trigger customer notifications, won’t have SLAs, and any work category-specific features (such as Opsgenie integration, deployment gating etc) will no longer work.<br/>
        <div style="padding:5px"></div>
        To delete an email request type, you will need to first turn off email requests for the service project, then follow the steps above. To turn off email requests for a service project:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project settings</b>, then <b>Email requests.</b></li>
        <li>Select <b>More actions (...)</b> and then <b>Disable all email requests.</b></li>`,
		contentType: 'procedural',
	},
	{
		question: 'What are custom fields?',
		answer: `Custom fields are additional fields that you can create and add to issues and request forms to capture specific information that is not collected by the default fields. Custom fields can be used across multiple projects and issue types.<br/>
        <div style="padding:5px"></div>
        These fields can be tailored to your organization's needs, enabling you to gather relevant data for each request or issue, while enhancing reporting, sorting, and issue management capabilities. For example, you might create a custom field for ‘Department’ to track the department each issue relates to.<br/>
        <div style="padding:5px"></div>
        Custom fields can vary in type (eg, text fields, date pickers, drop-down menus, and check-boxes), providing flexibility in how information is collected and processed.<br/>
        <div style="padding:5px"></div>
        Only Jira admins can create and edit custom fields.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'How do custom fields relate to request types',
		answer: `Each request type has an intake form comprising its own set of fields, which typically include custom fields.<br/>
        <div style="padding:5px"></div>
        Custom fields play a crucial role in defining request types as they help to capture information relevant to a particular type of request. For example, an ‘Employee transfer’ request type might include custom fields like ‘Transfer reason’ or ‘Transfer duration’.<br/>
        <div style="padding:5px"></div>
        Changes to custom fields in request types can affect both the customer’s view of the form in the portal, and the agent’s issue view.<br/>
        <div style="padding:5px"></div>
        You have the flexibility to add more context to each custom field (eg, changing its customer-facing name and including instructions), making it easier for customers to provide the right information.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What do I need to know about using custom fields?',
		answer: `Using custom fields effectively can greatly improve your issue management and reporting capabilities. As you start working with custom fields, we recommend you:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Limit the number: it might be tempting to create a custom field for every piece of data, but too many fields can clutter your interface and slow down Jira. Carefully consider the information you need before creating a new field.</li>
        <li>Reuse existing fields: before creating a new custom field, check if there is an existing field that could serve the same purpose. Changing the name and description of a custom field in the request form means the same custom field can appear differently in multiple places. This flexibility allows for custom fields to be reused more easily.</li>
        <li>Keep them simple: custom fields should be easy to understand and use. Avoid complex naming conventions and opt for clear, descriptive names that all users can understand.</li>
        <li>Use appropriate field types: Jira offers a variety of field types like text fields, date pickers, and drop-down menus. Choose the one that best suits the data you want to collect.</li>
        <li>Set field permissions: keep sensitive data secure by setting permissions on who can view and edit custom fields.</li>
        <li>Regularly review and clean up: over time, some custom fields may become redundant. Regularly review your fields and remove any that are no longer needed.</li>
        </ul>`,
		contentType: 'best-practices',
	},
	{
		question: 'When should I use an existing custom field vs a new one?',
		answer: `Use an existing custom field when the information to be captured is already covered by a field. For example, reusing a ‘Department’ custom field ensures data consistency across projects and reduces the clutter of excess fields.<br/>
        <div style="padding:5px"></div>
        A custom field can have variations across projects if new custom field contexts are created. This is a great way use the same field in different projects for different applications.<br/>
        <div style="padding:5px"></div>
        Create a new custom field to capture unique information not covered by an existing field and not applicable to other issues or projects. Or when it enhances data analysis, reporting, or issue management specific to a project's or team's needs.<br/>
        <div style="padding:5px"></div>
        Managing too many custom fields can be complex. Balance the creation and reuse of custom fields to optimize your project’s configuration and performance.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'What’s the problem with creating many custom fields?',
		answer: `Creating too many custom fields can lead to several problems.<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Data inconsistency: it can make your project complex and overwhelming, leading to errors and inefficiencies. Admins might not know which fields to use, and customers may be confused about what information is required, causing problems with data quality.</li>
        <li>Poor system performance: too many custom fields can slow down issue creation, searches, reporting, and issue loading times, degrading the overall experience.</li>
        <li>Maintenance challenges: it’s harder to maintain, update, and ensure the relevance of each field. It can also complicate the configuration and maintenance of your Jira instance, making it more challenging to manage.</li>
        <li>Reporting complexity: custom fields that look similar may impact JQL and the ability to report across projects, as it may not be clear which fields to use.</li>
        </ul>
        <div style="padding:5px"></div>
        Consider using a smaller set of well-defined, versatile custom fields. Use schemes and configurations to apply them in different contexts. And review your custom fields regularly to ensure they're still necessary and relevant.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'Who can create custom fields?',
		answer: `Custom fields can only be created, modified, or deleted by users with Jira administration permissions.<br/>
        <div style="padding:5px"></div>
        Jira admins have a comprehensive view of an instance’s needs and usage, which is essential for effective field management. Restricting custom field creation to this role helps to:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>maintain consistency and reduce the duplication of fields.</li>
        <li>avoid the creation of an excessive number of fields which could potentially slow down Jira’s performance and negatively impact reporting.</li>
        </ul>
        <div style="padding:5px"></div>
        Managing many custom fields can be complex so before creating a new one, consider whether reusing an existing custom field is an option.<br/>
        <div style="padding:5px"></div>
        Changing the field name and description in the intake form can help to make field reuse easier.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'How do custom fields interact with other Jira Service Management features?',
		answer: `Custom fields are primarily used to capture information from customers seeking help and to provide more information to agents as they work on those requests. Other uses for custom fields include:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Reporting: custom fields can be used to filter, sort and create reports that help you showcase your work.</li>
        <li>Automation: custom fields can be used with automation or advanced workflow features (validators, conditions and post-functions) to make your team more effective and efficient.</li>
        <li>Jira configuration: custom field behaviour is moderated by the issue type which is impacted by the set up of your screens, field configurations and contexts.</li>
        </ul>`,
		contentType: 'conceptual',
	},
	{
		question: 'Add a new custom field',
		answer: `It’s best to reuse custom fields whenever possible so before adding a new one, check whether the field exists to avoid creating a duplicate.<br/>
        <div style="padding:5px"></div>
        You need to be a Jira administrator to add custom fields.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Select any request type, then select <b>Create new custom fields</b> at the bottom of the right side panel (it will open a new tab).</li>
        <li>OR navigate to <b>Jira settings</b>, then <b>Issues</b>. In the <b>Fields</b> section on the navigation on the left, select <b>Custom fields</b>.</li>
        <li>Select <b>Create custom field</b>.</li>
        <li>Select <b>All</b>. You'll be presented with a selection of field types to choose from. These include text fields, number fields, date fields, dropdowns, checkboxes, and more.</li>
        <li>Select the type of field you want to create, then select <b>Next</b>.</li>
        <li>Name your field and add a description.</li>
        <li>If required, configure any further settings for the new custom field.</li>
        <li>Select <b>Create</b>.</li>
        <li>You’ll be taken to the <b>Screens</b> page, which is not mandatory, so you can close the tab. Associating the field with screens determines where the field will be available. Make your selections and select <b>Update</b>, at the bottom of the screen.</li>
        <li>Refresh the request types page and the custom field will now be available.</li>
        </ol>
        <div style="padding:5px"></div>
        Remember, the custom fields you create can be used across multiple projects and issue types, so it's a good idea to give them clear, descriptive names.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Edit a custom field',
		answer: `Only Jira admins can edit custom fields.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Navigate to <b>Jira settings</b>, then <b>Issues</b>. In the <b>Fields</b> section on the navigation on the left, select <b>Custom fields</b>.</li>
        <li>For the field you want to edit, select <b>More actions (...)</b>, then select <b>Edit details</b>. You can modify the field's name, description, or search template, then select <b>Update</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Note that you can't change the field's type (for example, making a single-select field into a multi-select field). To change a field's type, recreate the field using the desired type.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Delete a custom field',
		answer: `Only Jira admins can delete custom fields. You need to move custom fields to the trash before permanently deleting them. When you move a field to the trash, it appears to your users as if it's been deleted. You may still see the field in some of your site’s schemes, but the field itself is hidden from your user’s view.<br/>
        <div style="padding:5px"></div>
        To move a custom field to the trash:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Navigate to <b>Jira settings</b>, then <b>Issues</b>. In the <b>Fields</b> section on the navigation on the left, select <b>Custom fields</b>.</li>
        <li>Find the field you want to remove and select <b>More actions (...)</b>, then <b>Move to trash</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        After you trash a field you have 60 days to view or restore the field before it is permanently deleted. If you’re confident that you no longer need a custom field, you can permanently delete it by:<br/>
        <div style="padding:5px"></div>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Navigating to <b>Jira settings</b>, then <b>Issues</b>. In the <b>Fields</b> section on the navigation on the left, select <b>Custom fields</b>.</li>
        <li>Select the <b>Trashed</b> tab at the top of the table.</li>
        <li>Find the field you want to delete and select <b>Actions (...)</b>, then <b>Permanently delete now</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Permanently deleting a custom field also deletes all the data stored against that field. This action can’t be undone. If you want to keep field data for historical records, we recommend you back up your Jira site before permanently deleting fields.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'How do I manage the settings for custom fields?',
		answer: `When a new custom field is created, an associated custom field context is automatically created. Custom fields are mainly configured through these contexts.<br/>
        <div style="padding:5px"></div>
        Contexts determine which projects and issue types a field appears in, as well as any default values or dropdown options. They allow you to customize how a field appears per project, enhancing reusability.<br/>
        <div style="padding:5px"></div>
        Note that:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>A custom field can only have a single context per project.</li>
        <li>Restricting the issue types can make a field not appear in a project by issue types.</li>
        </ul>
        <div style="padding:5px"></div>
        To view the list of all the custom fields in your project, go to <b>Jira Settings</b> and select <b>Issues</b>, then <b>Custom Fields</b>.<br/>
        <div style="padding:5px"></div>
        To change a custom field’s context, select <b>More actions (...)</b> for the field you want to update, then select <b>Contexts and default value</b>.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'Why can’t project admins see all the custom fields on an instance?',
		answer: `Project admins may not be able to see all the custom fields on an instance for these reasons:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Permissions: only users with Jira administration permissions can view and manage all custom fields. Project admins will only see the fields that request type has access to (which is defined by the issue type).</li>
        <li>Field configuration: custom fields may be hidden based on field configurations which determine which fields are visible or hidden in a project.</li>
        <li>Issue type: some custom fields are specific to certain issue types, so if the issue type isn't part of a project, project admins won't see these fields.</li>
        <li>Screen configuration: custom fields must be included in a screen, otherwise it won’t be visible.</li>
        </ul>`,
		contentType: 'best-practices',
	},
	{
		question: 'How do I prepare for importing data into custom fields? ',
		answer: `Jira admins can import data into custom fields which can be useful when migrating information from another system or when there’s large amounts of data to input.<br/>
        <div style="padding:5px"></div>
        Before you start importing, ensure:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>your data is in the correct format and the custom fields you’re importing into have been created.</li>
        <li>the type of data you're importing matches the type of the custom field. For example, if you’re importing into a selection list field (like a drop-down or multi-select list), the imported values must exactly match the list options.</li>
        <li>you prepare and review your data carefully. Consider running a test import with a small data set first to ensure everything works as expected.</li>
        </ul>`,
		contentType: 'best-practices',
	},
	{
		question: 'Import data to custom fields',
		answer: `Here’s the steps for importing data into custom fields:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Organize your data in a CSV (Comma Separated Values) file. Each column should correspond to a custom field. You may need to add new custom fields to match the fields in your CSV file.</li>
        <li>As a Jira admin, navigate to <b>Jira settings</b>, then <b>System</b>. In the navigation on the left, select <b>External System Import</b>.</li>
        <li>Select the <b>CSV</b> option to start the import wizard.</li>
        <li>Select <b>Choose file</b> to locate and upload your CSV file, then select <b>Next</b>.</li>
        <li>Map the columns in your CSV file to the corresponding fields to assign your data to your custom fields.</li>
        <li>Your data will be validated. If there are any errors, you'll have an opportunity to correct them. Once validated, select <b>Begin import</b>.</li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'What are forms?',
		answer: `A form is a collection of fields and other elements that can be added to request types to assist with request intake, and to existing issues to gather more information.<br/>
        <div style="padding:5px"></div>
        Forms are crucial in helping to capture the information needed to resolve requests efficiently without adding more custom fields to your instance.<br/>            
        <div style="padding:5px"></div>
        Note that:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Form fields appear only on the form they are added to, and can be mapped to Jira custom fields.</li>
        <li>Forms can be built using conditional logic and rich formatting to let you show or hide fields, set field validation, and customize layouts with headings or tables.</li>
        <li>Forms are ideal for when you need to record the capture of information at a particular point in time.</li>
        </ul>
        <div style="padding:5px"></div>
        To start using forms, select <b>Project settings</b>, then <b>Forms</b> from the navigation on the left.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What features do forms have?',
		answer: `Forms have a range of features designed to streamline the request process, gather relevant information, and improve issue resolution. Some key features include:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>a range of field types such as text, drop-down menus, date pickers, and checkboxes that can be arranged in customized layouts with headings and tables.</li>
        <li>conditional logic to let you show or hide sections based on previous responses.</li>
        <li>validation rules to ensure accurate responses.</li>
        <li>the ability to add attachments for additional context.</li> 
        <li>easy form field creation that can performed without Jira administration permissions, with fields appearing only on the form they are added to, not your site. They can be linked to Jira fields if needed.</li>
        <li>more than 200 form templates, making it quick and easy for you to create your form.</li>
        </ul>`,
		contentType: 'conceptual',
	},
	{
		question: 'Where can forms be used?',
		answer: `You can choose where this form appears and how it is used in order to raise requests or create issues.<br/> 
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Attach to a request type: add a form to a request type and it’ll appear for customers to fill in when they raise a request in the portal.</li>
        <li>Create a shareable link: create issues using a URL that can be shared externally so that forms can be filled out by any licensed, logged-in Jira users and create issues.</li>
        </ul>
        <div style="padding:5px"></div>
        In the issue view, forms appear under <b>Attached forms</b> and all behave the same way, regardless of whether they were filled out in the portal, used to create the issue, or added to the issue manually.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'When should I add a form to a request type?',
		answer: `Attach a form to a request type when you need specific, structured information that goes beyond what is typically captured by the request type's default fields. The form will appear for customers when they raise a request from the portal.<br/>
        <div style="padding:5px"></div>
        Adding a form allows you to include conditional sections, rich formatting, and custom layouts to request forms. This is especially useful for complex requests requiring detailed inputs, such as HR requests with multiple data points, or any situation where clarity and precision in the request submission are crucial for efficient resolution.<br/>
        <div style="padding:5px"></div>
        Forms can standardize the data you collect and reduce the need for follow-up queries, improving your team’s efficiency.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'What happens when I add a form to a request type?',
		answer: `When Jira or project admins add a form as part of the request form on a request type, it'll appear as one form for customers to fill in when they raise a request in the portal.<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Form fields will look like regular Jira fields – customers won’t notice that the form is a separate object within the request form they fill in. However, after the request is raised the form will be separated and customers will see it under <b>Attached forms</b> on their request.</li>
        <li>The form can be used on its own for raising requests, or used alongside existing Jira fields.</li>
        <li>If you've added a form to a request type without linking Jira fields, the form fields will appear underneath Jira fields in the portal and issue view.</li>
        </br>`,
		contentType: 'conceptual',
	},
	{
		question: 'How do forms work in the issue view?',
		answer: `All forms appear under <b>Attached forms</b> and behave the same way in the issue view, regardless of whether they were filled out in the portal, created using a link, or added to the issue either manually or by using automation.<br/>
        <div style="padding:5px"></div>
        In the issue view:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>when a request is raised in the portal, information entered in Jira fields will appear at the top of the issue, and the completed form will appear under <b>Attached forms</b>.</li>
        <li>new forms added to issues are set to <b>Internal</b> by default, meaning only agents and admins can see the forms. Forms can be made external, allowing customers to see the form in the portal.</li>
        <li>depending on the form setttings, an agent or admin may be able to <b>Edit</b> a submitted form, or <b>Open customer edits</b> to allow a customer to update and resubmit their answers.</li>
        <li>a PDF of the form will be saved and attached to the issue each time it’s submitted (if this is enabled in the form builder <b>Settings</b>). These PDFs will appear under <b>Attachments</b>.</li>
        </ul>`,
		contentType: 'conceptual',
	},
	{
		question: 'Create and customize a blank form',
		answer: `To create a new blank form and add the fields you need, you’ll need Jira admin or project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Forms</b>.</li>
        <li>Select <b>Create form</b>, then <b>Create blank</b>.</li>
        <li>Enter a name for your form.</li>
        <li>Select <b>Add field</b> and choose your desired field types.</li>
        <li>Edit fields by selecting them and updating their attributes in the right-hand panel.</li>
        <li>Select <b>Section</b> to group fields together by adding a section above and below your desired fields. To make a section conditional, select <b>Conditionally</b> under <b>Show Section</b> in the right-hand panel for sections above the field group. These fields must be radio, checkbox, or dropdown field types.</li>
        <li>Use the toolbar at the top to add rich formatting elements such as tables, column layouts, or information panels.</li>
        <li>Select <b>Preview</b> at the top left to see what your form looks like, and test any conditional logic or validation.</li>
        <li>To finish, select <b>Save changes</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Note that sections that are added to a table or layout will break the structure of your form.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Create a form using a template',
		answer: `To create a form using a template, you’ll need Jira admin or project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Forms</b>.</li>
        <li>Select <b>Create form</b>, then <b>Create from template</b>.</li>
        <li>Browse the categories or search for the template you want to use, then <b>Preview</b> to see what the form looks like.</li>
        <li><b>Select</b> to use the template as is, or make changes to it by adding or removing elements.</li>
        <li>Select <b>Preview</b> to see what your form looks like, and test any conditional logic or validation.</li>
        <li>To finish, select <b>Save changes</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        You can access the template library again to select another while in the form builder by selecting <b>Templates</b>.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Edit a form',
		answer: ` To edit a form, you’ll need Jira admin or project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Forms</b>.</li>
        <li>Select the name of the form you want to edit, or <b>More actions (...)</b> then <b>Edit</b>.</li>
        <li>Make your desired changes, then select <b>Save changes</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Edits won’t affect any existing instances where a form is already being used. If you’ve copied a form to another project, any changes made won’t be reflected across any of its copies.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'What do I need to know about linking form fields and Jira fields?',
		answer: `Form fields and Jira fields each behave differently but can be linked together.<br/>
        <div style="padding:5px"></div>
        You can link form fields to most Jira fields to ensure automation, reporting, JQL, and other processes work as usual, however certain criteria must be satisfied or your form may not work as expected.<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Jira fields can’t be on a request form and a form at the same time. eg, if you have added a form to the ‘Get IT help’ request type, the ‘Description’ Jira field can’t be on the request form and linked to a form field as it will appear twice for customers.</li>
        <li>Jira fields can only be linked to once in the same form. eg, if your form field is linked to the Jira field ‘Affected Services’, another form field can’t be linked to it too.</li>
        <li>Linked fields must be the same field type. eg, if your form field is multiple choice, you can’t link it with a text Jira field.</li>
        <li>Required fields cannot be used in a conditional section.</li>
        </ul>`,
		contentType: 'best-practices',
	},
	{
		question: 'Link a form field to a Jira field',
		answer: `To link a form field to a Jira field, you’ll need Jira admin or project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From the navigation on the left, select <b>Project settings</b>, then <b>Forms</b>.</li>
        <li>Select the form you wish to edit.</li>
        <li>Select the form field you wish to link to a Jira field.</li>
        <li>In the right hand panel, under <b>Linked Jira field</b>, select the Jira field you want to connect. Any form fields that are linked will be represented with a link symbol. If this Jira field is already on the request type the form is associated with, it will be removed from the request type so it can be linked to a form field in this form instead.</li>
        <li>Select <b>Save changes</b>.</li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Add a form to an issue',
		answer: `To add a form to an existing issue:<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Go to the issue you want to add a form to.</li>
        <li>Select <b>Add form</b> at the top of the issue.</li>
        <li>Search for and select the form you want to add to the issue.</li>
        <li>Select <b>Add</b>. Forms added to issues are set to <b>Internal</b> by default, meaning that only agents and admins can see the form on the issue.</li>
        <li>To make the form visible to the customer, select <b>Make form external</b> from the <b>More actions (...)</b> menu.</li>
        <li>To make the form visible only to agents and admins again, select <b>Make form internal</b> from the <b>More actions (...)</b> menu.</li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Edit a form on an issue',
		answer: `Depending on the form’s state, you may need to be a project admin to edit a form after it’s been submitted.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Go to the issue with the form you want to edit.</li>
        <li>Under <b>Attached forms</b>, select the form you wish to edit.</li>
        <li>Select <b>Edit</b> from the <b>More actions (...)</b> menu.</li>
        <li>Make your desired changes.</li>
        <li>To save your progress without submitting the form, select <b>Save</b>.</li>
        <li>When you’re finished, select <b>Submit</b>.</li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Can agents or customers fill in forms for existing issues?',
		answer: `Yes, both agents and customers can fill in or edit forms for existing issues.<br/>
        <div style="padding:5px"></div>
        Agents can find forms in the <b>Attached forms</b> section in the issue view. Or the agent can add a form to capture more information from the customer by selecting <b>Add form</b> at the top of the issue view.<br/>
        <div style="padding:5px"></div>
        Agents can select <b>Edit form</b> to fill in the form fields with any relevant information they have gathered. They can  also control when customers can edit a form in the portal by selecting <b>Open customer edits</b>.<br/>
        <div style="padding:5px"></div>
        Customers can fill in a form (if it has been opened for customer edits) that shows at the bottom of the request in the portal.<br/>     
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Selecting <b>Save</b> after filling in the form saves any changes, and allows for further edits to be made.</li>
        <li>Selecting <b>Submit</b> after filling in the form means no further changes can be made.</il>
        </ul>`,
		contentType: 'conceptual',
	},
	{
		question: 'What are portal groups?',
		answer: `Portal groups are like folders that are used to group and organize related request types (and their forms) from a single project together on the portal.<br/>
        <div style="padding:5px"></div>
        They help customers navigate easily to the right request form and submit their requests efficiently.<br/>
        <div style="padding:5px"></div>
        For example, if you have request forms for ‘Employee onboarding', ‘Employee offboarding’, and ‘Employee transfer’, you might put these in a portal group called ‘Employee changes’ so that all the related request forms can be found in one place.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What’s the relationship between request types and portal groups?',
		answer: `Request types and portal groups work together to help organize and manage how customers raise requests from the portal.<br/>
        <div style="padding:5px"></div>
        Portal groups are like folders that are used to group related request forms (from a single project) together on the portal. They help customers navigate easily to the right request form and submit their requests efficiently.<br/>
        <div style="padding:5px"></div>
        For example, if you have request forms for ‘Employee onboarding', ‘Employee offboarding’, and ‘Employee transfer’, you might put these in a portal group called ‘Employee changes’ so that all the related request forms can be found in one place.<br/>
        <div style="padding:5px"></div>
        Note that:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Request forms that are not added to any portal groups will be hidden and won’t show up on the portal.</li>
        <li>One request form can be added to multiple portal groups which can be useful if the request form is relevant for several use cases.</li>
        <li>You should use topics, rather than portal groups, if you need to group request forms from across multiple projects.</li>
        </ul>
        <div style="padding:5px"></div>`,
		contentType: 'conceptual',
	},
	{
		question: 'What should I name my portal group?',
		answer: `A well-named portal group assists customers to easily access the help they need. It also aids in managing and grouping request forms effectively.<br/>
        <div style="padding:5px"></div>
        Choose a name for your portal group that is clear and relevant to your customers and your team. The name should reflect the portal group's purpose and the services or help customers can expect to find there. Avoid technical jargon that relies on assumed knowledge.<br/>
        <div style="padding:5px"></div>
        For example, if a group handles HR requests, you might name it ‘HR Services’. Or you might be more specific and create a portal group called ‘Employee changes’ where customers can find help relating to employee offboarding or transfers.<br/>
        <div style="padding:5px"></div>
        After you've decided on a name, create the new portal group in project settings and revise your portal group names as your team or services evolve.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'How should I configure portal groups?',
		answer: `When configuring your portal groups, think about how you might group similar or relevant request types (and their forms) together, making it more intuitive for customers to find the service they need. This might be by service type, or by common themes.<br/>
        <div style="padding:5px"></div>
        Draw upon your understanding of your customers' needs and how they would typically navigate your services. This can provide valuable insights to refine your portal's organization.<br/>
        <div style="padding:5px"></div>
        Once you know the groups you want to create, name each one so that it clearly indicates the kinds of requests that it covers. For instance, a portal group for HR-related issues could be named 'HR Services'.<br/>
        <div style="padding:5px"></div>
        A well-organized portal is dynamic. Regularly review your groups and adjust them based on user feedback and changes in your services. This ensures your portal stays relevant and easy to use.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'Create a portal group',
		answer: `You need to be a Jira administrator to create a portal group.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project Settings</b>, then <b>Portal settings</b>.</li>
        <li>Select the <b>Portal groups</b> tab on the right, then select <b>Create group</b>.</li>
        <li>Enter a name for the group.</li>
        <li>Add the request form(s) you want to appear in the group, then select <b>Save</b>.</li>
        <li>Reorganize the order of your portal groups by selecting the handle on the left of each portal group and dragging it to the desired position.</li>
        <div style="padding:5px"></div>
        You can also create a portal group by going to <b>Project Settings</b>, then <b>Request types</b>. Select <b>More actions (...)</b> next to a request type, then in the <b>Portal group</b> section select <b>Edit</b>, then <b>Create group</b>.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Can I assign a request type to more than one portal group?',
		answer: `Yes, you can assign a request form to as many portal groups as you like. This is because the goal of portal groups is to help your customers easily find the help they need.<br/>
        <div style="padding:5px"></div>
        There may be several portal groups that seem relevant for a particular type of request, so it might make sense to surface the same request form in multiple portal groups.<br/>
        <div style="padding:5px"></div>
        However, only do this if it assists your customers to find help more efficiently. In most cases, request forms will only be assigned to one portal group.<br/>
        <div style="padding:5px"></div>
        Be sure to review and update your portal groups regularly for optimal organization.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'Delete a portal group',
		answer: `To delete a portal group you’ll need project admin permissions.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project Settings</b>, then <b>Portal settings</b>.</li>
        <li>Select the <b>Portal groups</b> tab on the right.</li>
        <li>Hover over the portal group you wish to delete, select the trash icon, then select <b>Confirm</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        This will permanently delete the group and can't be undone.<br/>
        <div style="padding:5px"></div>
        There will be a warning about any request forms that will no longer be visible in your portal. You may want to add these to another portal group.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'What’s the relationship between a help center and a portal?',
		answer: `When you sign up for a Jira Service Management site, it comes with a help center. This is like a central hub where your customers go to access information and raise requests through specific portals.<br/>
        <div style="padding:5px"></div>
        Site admins or Jira admins can set up and manage a help center, where they can:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>create topics to group help resources from multiple portals.</li>
        <li>feature, sort, hide, or show portals.</li>
        <li>tailor the branding to match their team or organization’s identity.</li>
        </ul>
        <div style="padding:5px"></div>
        For each service project, a corresponding portal is automatically created. Customers can view the portal for each service project they have access to and can return to the help center via the portal’s breadcrumb link.<br/>
        <div style="padding:5px"></div>
        Project admins manage these portals, where they can:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>choose to hide or show the portal on the help center.</li>
        <li>share the portal URL with customers by selecting <b>Channels</b> and copying the portal URL.</li>
        <li>use portal groups to group together related request types (and their forms).</li>
        </ul>`,
		contentType: 'conceptual',
	},
	{
		question: 'Feature portals in your help center',
		answer: `Featuring portals at the top of the help center’s home page is useful for promoting portals that are business-critical or are commonly-used by customers. You can feature up to 15 portals in your help center.<br/>
        <div style="padding:5px"></div>
        You need to be a Jira administrator to feature portals in your help center.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Select <b>Settings</b>, then <b>Products</b>.</li>
        <li>Select <b>Jira Service Management</b>, then <b>Configuration</b>.</li>
        <li>Under <b>Customize your help center</b>, select <b>Edit the layout of your help center</b>.</li>
        <li>On the <b>Edit home page layout</b> page, select the star icon for the portal(s) you want to feature.</li>
        <li>Drag and drop to reorder your portals in the featured section.</li>
        <li>Use the <b>Sort by</b> field to sort your remaining portals by popularity or name.</li>
        <li>Select <b>Publish layout</b> to make the changes you made show up on the help center. Or select <b>Cancel</b> to discard any changes and revert to the last published version of the home page layout.</li>
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'What are topics?',
		answer: `A topic lets Jira admins group request forms, knowledge base articles, and external resources from across multiple portals or projects.<br/>
        <div style="padding:5px"></div>
        There can be up to 15 topics in a help center and each topic that is created will have a unique URL and a separate page of its own.<br/>
        <div style="padding:5px"></div>
        Creating topics can improve self-service by helping your customers to quickly find the relevant resources or request forms they need.<br/>
        <div style="padding:5px"></div>
        For example, a ‘New hire basics’ topic could group all the help resources a new hire would need in one place. To this topic you could add the ‘Request software’ request type from your IT team’s portal, and articles about company policies from your legal team’s portal.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What’s the difference between topics and portal groups?',
		answer: `Portal groups and topics perform a similar function by grouping together items in the help center, making it easier for your customers to quickly find what they need in one place. However, they do have some differences.<br/>
        <div style="padding:5px"></div>
        Portal groups are used to group related request types (and their forms) within a single project.<br/>
        <div style="padding:5px"></div>
        Whereas topics can be used to group request forms, knowledge base articles, and external resources from across multiple portals or projects, allowing you to create more customized groups.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'What are workflows?',
		answer: `A workflow is the series of steps within a defined path that your issues take through their lifecycle from creation to completion. It comprises a set of statuses, transitions, and rules that define the processes for handling and resolving issues, ensuring that all necessary steps are taken before issues are closed.<br/>
        <div style="padding:5px"></div>
        For example, a simple workflow might look like this: To Do > In Progress > Done. An issue will start in the ‘To Do’ status, and will be transitioned to the other statuses as it is worked on.<br/>
        <div style="padding:5px"></div>
        Workflows are typically customized to match the specific work processes of a team or project, which might involve adding more steps, setting rules for who can move tasks, or automating certain actions.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'What are workflow statuses?',
		answer: `Statuses represent the steps an issue goes through in its lifecycle within a workflow, for example ‘Open’, ‘In Progress’, ‘Resolved’, and ‘Closed’.<br/>
        <div style="padding:5px"></div>
        Statuses help people understand the state of a piece of work. They appear in many views across your Jira site, linked Atlassian products, and third-party apps.<br/>
        <div style="padding:5px"></div>
        Each project template comes with default statuses which can be shared between request types. This allows you to search for and report on customer requests in the same status across any request type.<br/>
        <div style="padding:5px"></div>
        Using statuses to reflect the key steps (rather than every step) in a process can help your team to plan and manage their work, track progress, and identify potential bottlenecks.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'When should I create a new status vs use an existing one?',
		answer: `The goal is to balance the need for detailed process tracking with maintaining a workflow (and statuses) that is easy for all team members to use.<br/>
        <div style="padding:5px"></div>
        Identifying any unique stages in your workflow will help you to decide whether a new status is necessary or an existing one will suffice.<br/>
        <div style="padding:5px"></div>
        Create a new status when your workflow requires a unique stage that isn't represented by existing statuses, for example a new ‘Under review’ status that isn’t covered in your current status list.<br/>
        <div style="padding:5px"></div>
        Use an existing status if you can find one that accurately represents a stage in your workflow. Using an existing status whenever possible helps to keep things streamlined and user-friendly.<br/>
        <div style="padding:5px"></div>
        Too many statuses can lead to workflow complexity and confusion. It can also slow down issue resolution and complicate reporting and tracking, making it harder to get clear insights into project progress and performance.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'What is the workflow statuses tab in the request form?',
		answer: `The workflow statuses tab in a request type enables you to customize how the statuses in the workflow appear to your customers.<br/>
        <div style="padding:5px"></div>
        A simple example is a workflow comprising the stages ‘To do’, ‘In Progress’ and ‘Done’, but you could make the status names appear to customers as ‘Starting soon‘, ‘Working on it right now‘ and ‘We're all finished‘.<br/>
        <div style="padding:5px"></div>
        Changing the way a status name appears to customers can help them better understand what stage their request is at, and feel more confident that their work is being prioritized and progressed.<br/>
        <div style="padding:5px"></div>
        It’s also a way to shield your customers from your internal processes, typically by renaming multiple statuses to a single name. For example, statuses like ‘Under review’, ‘Pending’, and ‘Reopened’ could be renamed to ‘In progress’.<br/>
        <div style="padding:5px"></div>
        To view the workflow statuses tab, go to <b>Project Settings</b>, then <b>Request types</b>. Select the name of a request type, then select <b>Workflow statuses<b/>.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'How should I create workflows?',
		answer: `Creating workflows is all about mapping your team's processes to a series of steps that help to manage and automate tasks.<br/>
        <div style="padding:5px"></div>
        Here’s some recommendations to help with the successful creation of workflows:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>Define your process: document what your team's process looks like along with any requirements. Identify the key stages (statuses), who's involved, and what actions (transitions) move tasks from one stage to the next.</li>
        <li>Visualize your flow: use Jira's workflow editor to create your draft, with the steps moving from top to bottom or left to right. While there may be multiple paths, form your structure around the ‘happy path’.</li>
        <li>Simplicity is key: keep your workflows as simple as possible. A complex workflow can be confusing and hard to manage. You can always add more steps later if needed.</li>
        <li>Manage permissions: by default, anyone can transition issues (including customers if you have added a transition to the portal). You can control this using the permission scheme.</li>
        <li>Automate where possible: consider where automation can speed up processes, like auto-assigning issues or sending notifications.</li>
        <li>Test and refine: test the workflow with your team and key stakeholders to ensure it meets operational requirements before deploying it widely.</li>
        </ul>
        <ul style="padding-left: 20px;list-style-position: outside;">
        Remember, creating a workflow is an iterative process. You can refine and improve it as your team's needs change.<br/>`,
		contentType: 'best-practices',
	},
	{
		question: 'Create a new workflow for an existing issue type',
		answer: `Jira admins can create a new workflow and map it to an existing issue type. Follow this process when the issue type is in the project you’re working in.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>Select <b>Settings</b>, then <b>Products</b>.</li>
        <li>Select <b>Workflows</b> in the navigation on the left.</li>
        <li>Select <b>Add workflow</b>, then select <b>Create new</b>.</li>
        <li>Give your workflow a name and description. We recommend giving your workflow a name that describes the workflow and is similar to the name of the issue type you want to map it to.</li>
        <li>Add statuses and transitions to create your workflow:</li>
            <ol style="padding-left: 20px;list-style-position: outside;">
            <li>Select <b>Add status</b> to add a new step to the workflow (you can add existing statuses or create new ones).</li>
            <li>Select <b>Add transition</b> or drag from a node on one status to another to add a transition.</li>
            </ol>
        <li>In your project, select <b>Project settings</b>, then select <b>Workflows</b>.</li>
        <li>Select <b>Add workflow</b>, then select <b>Add existing</b>.</li>
        <li>Find and select the workflow you just created, then select <b>Next</b>.</li>
        <li>Select the issue type you want to map the workflow to, then select <b>Finish</b>.</li>
        <li>Select <b>Publish</b>.</li>
        <li>Select <b>Associate</b>.</li>
        <li>Select <b>Acknowledge</b>.</li> 
        </ol>`,
		contentType: 'procedural',
	},
	{
		question: 'Edit a workflow',
		answer: `Any default workflows can be customized to suit the specific needs of your team and business.<br/>
        <div style="padding:5px"></div>
        You must be a Jira administrator to edit a workflow.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, select <b>Project settings</b>, then <b>Workflows</b>.</li>
        <li>In the <b>Actions</b> column, select the edit icon for the workflow you want to update.</li>
        <li>Edit the workflow to add or remove statuses and transitions.</li>
        <li>Toggle between <b>View original</b> and <b>Edit draft</b> modes to review your changes. When you’re ready, select <b>Publish draft</b>.</li>
        </ol>
        <div style="padding:5px"></div>
        Note that the workflow will be updated wherever it is used. If you are sharing configuration across projects, any workflow edits will impact multiple projects. If configuration is not being shared, workflow edits may still impact multiple request types.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'Why can’t I see the workflow I want to add to a request type?',
		answer: `When you want to change the workflow for one of your request types, you can usually choose to replace the workflow with another from any service project on your Jira Service Management site. 
        <div style="padding:5px"></div>
        However, sometimes not all of your workflows will be available to select from the <b>Replace workflow</b> panel (after selecting the <b>More actions (...)</b> menu for the request type you’d like to update, then selecting <b>Replace with existing</b>).<br/>
        <div style="padding:5px"></div>
        Workflows will be filtered out of the list if using them would cause a conflict in your current project. They won’t appear if they:<br/>
        <ul style="padding-left: 20px;list-style-position: outside;">
        <li>already exist in your current project.</li>
        <li>connect to an issue type that:
            <ul style="padding-left: 20px;list-style-position: outside;">
            <li>is already used by another workflow (including the default workflow) in your current project.</li> 
            <li>is used by the default workflow in another project that shares the workflow scheme with the current project.</li>
            </ul>
        </li>
        </ul>
        <div style="padding:5px"></div>
        You can update your desired workflow so that it will show in the <b>Replace workflow</b> panel by changing the issue type of the workflow in one of your projects.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'Customize the workflow statuses for a request type',
		answer: `The flow of a request is determined by the workflow associated with a request’s issue type.<br/>
        <div style="padding:5px"></div>
        You can customize the names of these workflow statuses so that they are easier for your customers to understand. You can also map multiple statuses to a single status to simplify the appearance of the workflow.<br/>
        <div style="padding:5px"></div>
        You must be a project administrator to customize the workflow statuses that customers will see.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project settings</b>, and then <b>Request types</b>.</li>
        <li>Select the name of the request type whose workflow you want to customize.</li>
        <li>Select the <b>Workflow statuses</b> tab.</li>
        <li>Under <b>Status name to show customer</b>, enter the status display name that you want customers to see.</li>
        <li>Select <b>Save</b> to confirm your changes.</li>
        </ol>
        <div style="padding:5px"></div>        
        Note that a transition between two workflow statuses can be hidden from the customer by giving them the same status name.<br/>`,
		contentType: 'conceptual',
	},
	{
		question: 'Change the issue type assigned to a workflow',
		answer: `You must be a Jira administrator to change the issue type assigned to a workflow. Note that you can only assign issue types already used in the project as defined by the issue type scheme.<br/>
        <ol style="padding-left: 20px;list-style-position: outside;">
        <li>From your service project, go to <b>Project settings</b>, then <b>Workflows</b>.</li>
        <li>Find the workflow you’d like to update and select <b>Assign</b> in the issue types column.</li>
        <li>Select the issue types that you’d like to assign to your selected workflow. An issue type can only be assigned to one workflow at a time.</li>
        <li>Select <b>Finish</b> to assign the issue type to the workflow.</li>
        </ol>
        <div style="padding:5px"></div>        
        Any changes you’ve made will be unpublished. You can toggle between <b>View original</b> and <b>View draft</b> modes to review your changes. When you’re ready, select <b>Publish</b>.<br/>
        <div style="padding:5px"></div>        
        You may be prompted to change the current status of each issue so that it is compatible with the new workflows. Choose the new statuses, then select <b>Associate</b>.<br/>`,
		contentType: 'procedural',
	},
	{
		question: 'How can I use automation to streamline my workflows?',
		answer: `Automation can help streamline and make your workflows more efficient by automatically updating issue statuses. This allows your team to focus on more complex tasks.<br/>
        <div style="padding:5px"></div>        
        For example, you might choose a trigger like ‘Comment added’, which will result in the action ‘Transition issue’, which will move the issue’s status from ‘To do’ to ‘In progress’.<br/>
        <div style="padding:5px"></div>        
        Now, each time the trigger event occurs, the issue status will be automatically updated. You can create multiple automation rules to manage different scenarios in your workflows.<br/>
        <div style="padding:5px"></div>        
        Go to <b>Project settings</b> then <b>Automation</b> in your project to start setting up automation rules. Select <b>Create rule</b> and select a template or start from scratch. Define your triggers, conditions, and actions, then save and enable your rule.<br/>
        <div style="padding:5px"></div>        
        Remember, it's important to carefully plan and test your automation rules to ensure they work as expected.<br/>
        <div style="padding:5px"></div>        
        Instead of automation, you can also use workflow conditions, post functions, and validators to control a workflow transition.<br/>`,
		contentType: 'best-practices',
	},
];

export const findQuestion = (
	question: string,
): {
	question: string;
	answer: string;
	contentType: CONTENT_TYPE;
} => {
	const findResult = allQuestions.find((item) => item.question === question);
	if (findResult === undefined)
		return {
			question,
			answer: 'Placeholder',
			contentType: 'procedural',
		};
	return findResult;
};

export const essentialsGroupQuestionAndAnswer: {
	[key: string]: {
		question: string;
		answer: string;
		contentType: CONTENT_TYPE;
	}[];
} = {
	'Request types': [
		findQuestion('What are request types?'),
		findQuestion("What's the difference between a request type and an issue type?"),
		findQuestion('How many request types do I need?'),
		findQuestion('Which request types should I create?'),
		findQuestion('What are request type templates?'),
		findQuestion('What are some best practices for setting up request types?'),
		findQuestion('How many fields should I add to a request type?'),
		findQuestion('What is the request type name and description?'),
		findQuestion('What’s the difference between the request form and the issue view?'),
		findQuestion('What’s the relationship between request types and workflows?'),
		findQuestion('What’s the relationship between request types and portal groups?'),
		findQuestion('Create a request type'),
		findQuestion('Customize a request type'),
		findQuestion('Add a form to a request type'),
		findQuestion('Replace a request type’s workflow'),
	],
	'Custom fields': [
		findQuestion('What are custom fields?'),
		findQuestion('How do custom fields relate to request types'),
		findQuestion('What do I need to know about using custom fields?'),
		findQuestion('When should I use an existing custom field vs a new one?'),
		findQuestion('What’s the problem with creating many custom fields?'),
		findQuestion('Who can create custom fields?'),
		findQuestion('How do custom fields interact with other Jira Service Management features?'),
		findQuestion('Add a new custom field'),
		findQuestion('Edit a custom field'),
		findQuestion('Delete a custom field'),
		findQuestion('How do I manage the settings for custom fields?'),
		findQuestion('Why can’t project admins see all the custom fields on an instance?'),
		findQuestion('How do I prepare for importing data into custom fields? '),
		findQuestion('Import data to custom fields'),
	],
	Forms: [
		findQuestion('What are forms?'),
		findQuestion('What features do forms have?'),
		findQuestion('Where can forms be used?'),
		findQuestion('When should I add a form to a request type?'),
		findQuestion('What happens when I add a form to a request type?'),
		findQuestion('Add a form to a request type'),
		findQuestion('How do forms work in the issue view?'),
		findQuestion('Create and customize a blank form'),
		findQuestion('Create a form using a template'),
		findQuestion('Edit a form'),
		findQuestion('What do I need to know about linking form fields and Jira fields?'),
		findQuestion('Link a form field to a Jira field'),
		findQuestion('Add a form to an issue'),
		findQuestion('Edit a form on an issue'),
		findQuestion('Can agents or customers fill in forms for existing issues?'),
	],
	'Portal groups': [
		findQuestion('What are portal groups?'),
		findQuestion('What’s the relationship between request types and portal groups?'),
		findQuestion('What should I name my portal group?'),
		findQuestion('How should I configure portal groups?'),
		findQuestion('Create a portal group'),
		findQuestion('Can I assign a request type to more than one portal group?'),
		findQuestion('Organize request types into portal groups'),
		findQuestion('Delete a portal group'),
		findQuestion('What’s the relationship between a help center and a portal?'),
		findQuestion('Feature portals in your help center'),
		findQuestion('What are topics?'),
		findQuestion('What’s the difference between topics and portal groups?'),
	],
	Workflows: [
		findQuestion('What are workflows?'),
		findQuestion('What’s the relationship between request types and workflows?'),
		findQuestion('What are workflow statuses?'),
		findQuestion('When should I create a new status vs use an existing one?'),
		findQuestion('What is the workflow statuses tab in the request form?'),
		findQuestion('How should I create workflows?'),
		findQuestion('Create a new workflow for an existing issue type'),
		findQuestion('Edit a workflow'),
		findQuestion('Replace a request type’s workflow'),
		findQuestion('Add a new workflow to your request type using workflow templates'),
		findQuestion('Why can’t I see the workflow I want to add to a request type?'),
		findQuestion('Customize the workflow statuses for a request type'),
		findQuestion('Change the issue type assigned to a workflow'),
		findQuestion('How can I use automation to streamline my workflows?'),
	],
};
